import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";

import * as Api from "@/api";
import { HELP_MY_PARTNER } from "@/constants/HelpUrls";
import { NEW_PARTNER } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputSelect from "@/design/forms/InputSelect";
import InputText from "@/design/forms/InputText";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import { viStore } from "@sippy-platform/valkyrie";

import {
  Box,
  Card,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";

import partnerReducer from "../partners/partnerReducer";
import PbxCard from "../partners/PbxCard";

export default function MyPartner() {
  const dispatch = useDispatch();

  const auth = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_MY_PARTNER));
  }, [dispatch]);

  const {
    value: partner,
    dispatch: partnerDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_PARTNER,
    reducer: partnerReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
      supportEmail: string().email({ message: "Support email address isn't a valid format." }),
      salesEmail: string().email({ message: "Sales email address isn't a valid format." }),
    }),
    onSubmit: () => Api.storePartner(partner),
  });

  useEffect(() => {
    toggleLoadingState(true);

    Promise.resolve(Api.getPartner({ id: auth.partner.id }))
      .then((response) => partnerDispatch({ type: "set", value: response.data.partner }))
      .finally(toggleLoadingState);
  }, [auth.partner, partnerDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    partnerDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  return (
    <Page maxWidth="lg">
      <title>{`My Partner \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={<Avatar text={partner.name} icon={viStore} />}
          action={<SaveIndicator formState={formState} errors={allErrors} save={save} />}
          title={partner.name ? partner.name : "Loading..."}
          subheader={
            partner.name ? `${partner.pbxes?.length} ${partner.pbxes?.length === 1 ? "PBX" : "PBXes"}` : undefined
          }
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={partner.name} error={errors} />
          </Grid>
          {partner.id && partner.pbxes?.length > 0 && (
            <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
              <InputSelect
                id="defaultPbx"
                label="Default PBX"
                onChange={formHandler}
                value={partner.defaultPbx?.id}
                selectValue="id"
                selects={partner.pbxes}
                getOptionLabel={(option) => option.name}
                error={errors}
                hasEmpty={null}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              id="supportEmail"
              label="Support email"
              onChange={formHandler}
              value={partner.supportEmail}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              id="salesEmail"
              label="Sales email"
              onChange={formHandler}
              value={partner.salesEmail}
              error={errors}
            />
          </Grid>
        </FormGroup>
        {partner.defaultPbx && (
          <FormGroup title="Partner PBX">
            <Grid size={12}>
              <PbxCard pbx={partner.defaultPbx} />
            </Grid>
          </FormGroup>
        )}
        <FormGroup title="Accounts">
          <Grid size={12}>
            {partner.accounts?.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
                  gap: 1,
                }}
              >
                {partner.accounts?.map((user) => (
                  <Card variant="outlined" key={user.id}>
                    <List>
                      <ListItem disablePadding>
                        <ListItemButton
                          component={NavLink}
                          to={`/backstage/accounts/${user.id}`}
                          sx={{ borderRadius: 0 }}
                        >
                          <ListItemAvatar>
                            <Avatar text={`${user.firstname} ${user.lastname ?? ""}`} />
                          </ListItemAvatar>
                          <ListItemText primary={`${user.firstname} ${user.lastname ?? ""}`} />
                        </ListItemButton>
                      </ListItem>
                    </List>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography>You don't have any linked accounts yet.</Typography>
            )}
          </Grid>
        </FormGroup>
      </Stack>
    </Page>
  );
}
